/* eslint-disable react-refresh/only-export-components -- déclaration des routes (composants chargés à la demande) */
import { lazy } from 'react';
import { createBrowserRouter } from 'react-router-dom';

import { RequireAuth, RequirePermission } from '@/features/auth/guards';
import { NotFoundPage } from '@/features/errors/NotFoundPage';
import { RouteErrorPage } from '@/features/errors/RouteErrorPage';

import { AppLayout } from './layout/AppLayout';
import { ALL_ITEMS } from './layout/navigation';

// Chaque écran est chargé à la demande : premier affichage plus rapide sur connexion lente.
import HomeRoute from '@/features/editor/HomeRoute';

const LoginPage = lazy(() => import('@/features/auth/LoginPage'));
const AppearancePage = lazy(() => import('@/features/admin/AppearancePage'));
const PasswordChangePage = lazy(() => import('@/features/security/PasswordChangePage'));
const MfaSetupPage = lazy(() => import('@/features/security/MfaSetupPage'));
const AccountPage = lazy(() => import('@/features/security/AccountPage'));
const PatientsPage = lazy(() => import('@/features/patients/PatientsPage'));
const PatientCreatePage = lazy(() => import('@/features/patients/PatientCreatePage'));
const PatientDetailPage = lazy(() => import('@/features/patients/PatientDetailPage'));
const ReceptionPage = lazy(() => import('@/features/reception/ReceptionPage'));
const AgendaPage = lazy(() => import('@/features/appointments/AgendaPage'));
const UsersPage = lazy(() => import('@/features/admin/UsersPage'));
const RolesPage = lazy(() => import('@/features/admin/RolesPage'));
const AuditPage = lazy(() => import('@/features/admin/AuditPage'));
const AiJournalPage = lazy(() => import('@/features/assistant/AiJournalPage'));
const PractitionersPage = lazy(() => import('@/features/admin/PractitionersPage'));
const CatalogPage = lazy(() => import('@/features/admin/CatalogPage'));
const StructurePage = lazy(() => import('@/features/admin/StructurePage'));
const ClinicalWorklistPage = lazy(() => import('@/features/clinical/ClinicalWorklistPage'));
const EncounterWorkspacePage = lazy(() => import('@/features/clinical/EncounterWorkspacePage'));
const PrescriptionPrintPage = lazy(() => import('@/features/clinical/PrescriptionPrintPage'));
const NursingPage = lazy(() => import('@/features/nursing/NursingPage'));
const EmergencyPage = lazy(() => import('@/features/emergency/EmergencyPage'));
const InvoicesPage = lazy(() => import('@/features/billing/InvoicesPage'));
const InvoiceDetailPage = lazy(() => import('@/features/billing/InvoiceDetailPage'));
const CashDeskPage = lazy(() => import('@/features/cashdesk/CashDeskPage'));
const ReceiptPage = lazy(() => import('@/features/cashdesk/ReceiptPage'));
const EditorRecommendationsPage = lazy(
  () => import('@/features/recommendations/EditorRecommendationsPage'),
);
const CashJournalPrintPage = lazy(() => import('@/features/cashdesk/CashJournalPrintPage'));
const PharmacyPage = lazy(() => import('@/features/pharmacy/PharmacyPage'));
const LabWorklistPage = lazy(() => import('@/features/laboratory/LabWorklistPage'));
const LabOrderPage = lazy(() => import('@/features/laboratory/LabOrderPage'));
const LabReportPage = lazy(() => import('@/features/laboratory/LabReportPage'));
const LabCatalogPage = lazy(() => import('@/features/laboratory/LabCatalogPage'));
const StockPage = lazy(() => import('@/features/stock/StockPage'));
const ProcurementPage = lazy(() => import('@/features/procurement/ProcurementPage'));
const BedBoardPage = lazy(() => import('@/features/inpatient/BedBoardPage'));
const AdmissionPage = lazy(() => import('@/features/inpatient/AdmissionPage'));
const SummaryPrintPage = lazy(() => import('@/features/inpatient/SummaryPrintPage'));
const RoomsConfigPage = lazy(() => import('@/features/inpatient/RoomsConfigPage'));
const DepositReceiptPage = lazy(() => import('@/features/cashdesk/DepositReceiptPage'));
const ImagingWorklistPage = lazy(() => import('@/features/imaging/ImagingWorklistPage'));
const ImagingExamPage = lazy(() => import('@/features/imaging/ImagingExamPage'));
const ImagingReportPrintPage = lazy(() => import('@/features/imaging/ImagingReportPrintPage'));
const ImagingCatalogPage = lazy(() => import('@/features/imaging/ImagingCatalogPage'));
const StaffPage = lazy(() => import('@/features/hr/StaffPage'));
const MyPlanningPage = lazy(() => import('@/features/hr/MyPlanningPage'));
const ReportsPage = lazy(() => import('@/features/reports/ReportsPage'));
const InsurancePage = lazy(() => import('@/features/insurance/InsurancePage'));
const MaternityPage = lazy(() => import('@/features/maternity/MaternityPage'));
const PregnancyPage = lazy(() => import('@/features/maternity/PregnancyPage'));
const AmbulancePage = lazy(() => import('@/features/ambulance/AmbulancePage'));
const FinancePage = lazy(() => import('@/features/finance/FinancePage'));
const JournalsPage = lazy(() => import('@/features/journals/JournalsPage'));
const DeductionsPage = lazy(() => import('@/features/journals/DeductionsPage'));
const JournalPrintPage = lazy(() => import('@/features/journals/JournalPrintPage'));
const DeductionPrintPage = lazy(() => import('@/features/journals/DeductionPrintPage'));
const ProvidersPage = lazy(() => import('@/features/providers/ProvidersPage'));
const DraftStatementPrintPage = lazy(() => import('@/features/providers/StatementPrintPage'));
const PaidStatementPrintPage = lazy(() =>
  import('@/features/providers/StatementPrintPage').then((m) => ({
    default: m.PaidStatementPrintPage,
  })),
);
const NotificationsAdminPage = lazy(
  () => import('@/features/notifications/NotificationsAdminPage'),
);
const ClinicSettingsPage = lazy(() => import('@/features/admin/ClinicSettingsPage'));
const ActivityReportsPage = lazy(() => import('@/features/activity-reports/ActivityReportsPage'));
const ActivityReportPage = lazy(() => import('@/features/activity-reports/ActivityReportPage'));
const PlanningPrintPage = lazy(() => import('@/features/hr/PlanningPrintPage'));
const MyPlanningPrintPage = lazy(() => import('@/features/hr/MyPlanningPrintPage'));
const ReferenceListsPage = lazy(() => import('@/features/fichier/ReferenceListsPage'));
const StructureListsPage = lazy(() => import('@/features/fichier/StructureListsPage'));
const ReferenceTablesPage = lazy(() => import('@/features/fichier/ReferenceTablesPage'));
const StockValuationPrintPage = lazy(() => import('@/features/stock/StockValuationPrintPage'));
const MedicalFilesPage = lazy(() => import('@/features/medical-files/MedicalFilesPage'));
const MedicalFilePage = lazy(() => import('@/features/medical-files/MedicalFilePage'));
const MedicalFilePrintPage = lazy(() => import('@/features/medical-files/MedicalFilePrintPage'));
const PortalApp = lazy(() => import('@/features/portal/PortalApp'));
const EditorPage = lazy(() => import('@/features/editor/EditorPage'));
const EditorAiPage = lazy(() => import('@/features/assistant/EditorAiPage'));
const EditorGdtPage = lazy(() => import('@/features/gdt/EditorGdtPage'));
const GdtLibraryPage = lazy(() => import('@/features/gdt/GdtLibraryPage'));

const INPATIENT = [
  'inpatient.view',
  'inpatient.admit',
  'inpatient.transfer',
  'inpatient.discharge',
  'inpatient.beds',
];
const IMAGING = ['imaging.view', 'imaging.schedule', 'imaging.perform', 'imaging.report'];
const HR = ['hr.view', 'hr.manage', 'hr.planning', 'hr.planning.view', 'hr.leave.approve'];
const REPORTS = ['reports.activity', 'reports.medical', 'reports.finance', 'reports.hr'];

const LAB_READERS = ['lab.view', 'lab.collect', 'lab.enter', 'lab.validate'];
const INSURANCE = ['insurance.view', 'insurance.manage', 'insurance.coverage', 'insurance.claims'];
const MATERNITY = ['maternity.view', 'maternity.manage'];
const AMBULANCE = ['ambulance.view', 'ambulance.dispatch', 'ambulance.crew', 'ambulance.fleet'];
const FINANCE = ['finance.view', 'finance.manage', 'finance.approve'];

const guarded = (anyOf: string[], element: React.ReactNode) => (
  <RequirePermission anyOf={anyOf}>{element}</RequirePermission>
);

/** Droits d'un écran du menu : la même règle pour le menu et pour l'URL. */
const menu = (to: string): string[] => {
  const item = ALL_ITEMS.find((i) => i.to === to);
  if (!item?.anyOf) throw new Error(`Écran du menu sans droits déclarés : ${to}`);
  return item.anyOf;
};

export const router = createBrowserRouter([
  { path: '/connexion', element: <LoginPage />, errorElement: <RouteErrorPage /> },
  // Portail patient : application séparée, avec sa propre session (hors coque du personnel).
  { path: '/portail/*', element: <PortalApp /> },
  {
    element: <RequireAuth />,
    errorElement: <RouteErrorPage />,
    children: [
      { path: '/securite/mot-de-passe', element: <PasswordChangePage /> },
      { path: '/securite/double-authentification', element: <MfaSetupPage /> },
      {
        element: <AppLayout />,
        errorElement: <RouteErrorPage />,
        children: [
          {
            // Erreur dans un écran : message dans la page, le menu reste affiché.
            errorElement: <RouteErrorPage />,
            children: [
              { index: true, element: <HomeRoute /> },
              { path: 'mon-compte', element: <AccountPage /> },
              { path: 'editeur', element: guarded(menu('/editeur'), <EditorPage />) },
              { path: 'editeur/ia', element: guarded(menu('/editeur/ia'), <EditorAiPage />) },
              { path: 'editeur/gdt', element: guarded(menu('/editeur/gdt'), <EditorGdtPage />) },
              { path: 'gdt', element: guarded(menu('/gdt'), <GdtLibraryPage />) },
              {
                path: 'editeur/recommandations',
                element: guarded(menu('/editeur/recommandations'), <EditorRecommendationsPage />),
              },
              { path: 'dossiers', element: guarded(menu('/dossiers'), <MedicalFilesPage />) },
              {
                path: 'dossiers/:id',
                element: guarded(['medical_files.view'], <MedicalFilePage />),
              },
              {
                path: 'dossiers/:id/imprimer',
                element: guarded(['medical_files.view'], <MedicalFilePrintPage />),
              },
              {
                path: 'fichier/listes',
                element: guarded(menu('/fichier/listes'), <ReferenceListsPage />),
              },
              {
                path: 'fichier/tables',
                element: guarded(menu('/fichier/tables'), <ReferenceTablesPage />),
              },
              {
                path: 'fichier/titres-services',
                element: guarded(menu('/fichier/titres-services'), <StructureListsPage />),
              },
              { path: 'patients', element: guarded(['patients.view'], <PatientsPage />) },
              {
                path: 'patients/nouveau',
                element: guarded(['patients.create'], <PatientCreatePage />),
              },
              {
                path: 'patients/:id/*',
                element: guarded(['patients.view'], <PatientDetailPage />),
              },
              { path: 'accueil', element: guarded(['encounters.view'], <ReceptionPage />) },
              { path: 'agenda', element: guarded(['appointments.view'], <AgendaPage />) },
              {
                path: 'consultations',
                element: guarded(menu('/consultations'), <ClinicalWorklistPage />),
              },
              {
                path: 'passages/:id/:tab?',
                element: guarded(['encounters.view'], <EncounterWorkspacePage />),
              },
              {
                path: 'ordonnances/:id/imprimer',
                element: guarded(['medical_record.view'], <PrescriptionPrintPage />),
              },
              { path: 'soins', element: guarded(['nursing.manage'], <NursingPage />) },
              {
                path: 'urgences',
                element: guarded(menu('/urgences'), <EmergencyPage />),
              },
              { path: 'facturation', element: guarded(['billing.view'], <InvoicesPage />) },
              {
                path: 'facturation/:id',
                element: guarded(['billing.view'], <InvoiceDetailPage />),
              },
              {
                path: 'caisse',
                element: guarded(
                  ['cashdesk.operate', 'cashdesk.supervise', 'refunds.approve'],
                  <CashDeskPage />,
                ),
              },
              {
                path: 'caisse/journal',
                element: guarded(
                  ['cashdesk.operate', 'cashdesk.supervise'],
                  <CashJournalPrintPage />,
                ),
              },
              {
                path: 'caisse/recus/:id',
                element: guarded(['cashdesk.operate', 'cashdesk.supervise'], <ReceiptPage />),
              },
              { path: 'pharmacie', element: guarded(['pharmacy.dispense'], <PharmacyPage />) },
              { path: 'laboratoire', element: guarded(menu('/laboratoire'), <LabWorklistPage />) },
              {
                path: 'laboratoire/analyses/:id',
                element: guarded(LAB_READERS, <LabOrderPage />),
              },
              {
                path: 'laboratoire/analyses/:id/compte-rendu',
                element: guarded([...LAB_READERS, 'medical_record.view'], <LabReportPage />),
              },
              {
                path: 'laboratoire/catalogue',
                element: guarded(['lab.catalog'], <LabCatalogPage />),
              },
              {
                path: 'stock',
                element: guarded(['stock.view', 'stock.manage', 'stock.count'], <StockPage />),
              },
              {
                path: 'stock/etat/imprimer',
                element: guarded(
                  ['stock.manage', 'finance.view', 'reports.finance'],
                  <StockValuationPrintPage />,
                ),
              },
              {
                path: 'achats',
                element: guarded(menu('/achats'), <ProcurementPage />),
              },
              {
                path: 'hospitalisation',
                element: guarded(menu('/hospitalisation'), <BedBoardPage />),
              },
              { path: 'hospitalisation/:id', element: guarded(INPATIENT, <AdmissionPage />) },
              {
                path: 'hospitalisation/:id/compte-rendu',
                element: guarded(['medical_record.view'], <SummaryPrintPage />),
              },
              { path: 'admin/lits', element: guarded(['inpatient.config'], <RoomsConfigPage />) },
              {
                path: 'caisse/avances/:id',
                element: guarded(
                  ['cashdesk.operate', 'cashdesk.supervise'],
                  <DepositReceiptPage />,
                ),
              },
              { path: 'imagerie', element: guarded(IMAGING, <ImagingWorklistPage />) },
              { path: 'imagerie/examens/:id', element: guarded(IMAGING, <ImagingExamPage />) },
              {
                path: 'imagerie/examens/:id/compte-rendu',
                element: guarded([...IMAGING, 'medical_record.view'], <ImagingReportPrintPage />),
              },
              {
                path: 'imagerie/catalogue',
                element: guarded(['imaging.catalog'], <ImagingCatalogPage />),
              },
              { path: 'personnel', element: guarded(HR, <StaffPage />) },
              {
                path: 'personnel/planning/imprimer',
                element: guarded(HR, <PlanningPrintPage />),
              },
              { path: 'mon-planning', element: <MyPlanningPage /> },
              { path: 'mon-planning/imprimer', element: <MyPlanningPrintPage /> },
              { path: 'rapports', element: guarded(REPORTS, <ReportsPage />) },
              {
                path: 'rapports-activite',
                element: guarded(menu('/rapports-activite'), <ActivityReportsPage />),
              },
              {
                path: 'rapports-activite/:id',
                element: guarded(menu('/rapports-activite'), <ActivityReportPage />),
              },
              {
                path: 'admin/utilisateurs',
                element: guarded(['admin.users.manage'], <UsersPage />),
              },
              { path: 'admin/roles', element: guarded(['admin.roles.manage'], <RolesPage />) },
              { path: 'admin/audit', element: guarded(['audit.view'], <AuditPage />) },
              {
                path: 'admin/journal-ia',
                element: guarded(menu('/admin/journal-ia'), <AiJournalPage />),
              },
              { path: 'assurances', element: guarded(INSURANCE, <InsurancePage />) },
              { path: 'maternite', element: guarded(MATERNITY, <MaternityPage />) },
              { path: 'maternite/:id', element: guarded(MATERNITY, <PregnancyPage />) },
              { path: 'ambulance', element: guarded(AMBULANCE, <AmbulancePage />) },
              { path: 'finances', element: guarded(FINANCE, <FinancePage />) },
              { path: 'journaux', element: guarded(menu('/journaux'), <JournalsPage />) },
              { path: 'prestataires', element: guarded(menu('/prestataires'), <ProvidersPage />) },
              {
                path: 'prestataires/bulletins/:id',
                element: guarded(menu('/prestataires'), <PaidStatementPrintPage />),
              },
              {
                path: 'prestataires/bulletin-provisoire',
                element: guarded(menu('/prestataires'), <DraftStatementPrintPage />),
              },
              {
                path: 'journaux/imprimer',
                element: guarded(menu('/journaux'), <JournalPrintPage />),
              },
              {
                path: 'retenues-salaires/:id/imprimer',
                element: guarded(menu('/retenues-salaires'), <DeductionPrintPage />),
              },
              {
                path: 'retenues-salaires',
                element: guarded(menu('/retenues-salaires'), <DeductionsPage />),
              },
              {
                path: 'admin/notifications',
                element: guarded(['notifications.manage'], <NotificationsAdminPage />),
              },
              {
                path: 'admin/parametres',
                element: guarded(menu('/admin/parametres'), <ClinicSettingsPage />),
              },
              {
                path: 'admin/apparence',
                element: guarded(['admin.settings.manage'], <AppearancePage />),
              },
              {
                path: 'admin/praticiens',
                element: guarded(['schedules.manage'], <PractitionersPage />),
              },
              { path: 'admin/catalogue', element: guarded(['catalog.manage'], <CatalogPage />) },
              {
                path: 'admin/structure',
                element: guarded(['admin.settings.manage'], <StructurePage />),
              },
              { path: '*', element: <NotFoundPage /> },
            ],
          },
        ],
      },
    ],
  },
]);
